// src/pages/Manager/Week.jsx
// STATUS: active | UPDATED: 2026-09-24
// LOG:
//   2026-09-24  New. The last of the five sections of his management system
//               with nowhere to be done.
//
// HIS WEEKLY COACHING RHYTHM.
//
// Six things, reference since August. Same argument as the daily audit in 0029
// and the one on one and the morning meeting before it: a checklist you cannot
// tick is a poster.
//
// TWO OF THE SIX ARE MEASURED AND NOT ASKED ABOUT. The role-play one is
// countable from practice drills, and recognition is what the floor board
// counts. Asking a manager to tick a box about a thing already measured is how
// a checklist stops being believed.
//
// The other four happen in a CRM or on a floor. They are ticked, and the screen
// says plainly that a tick is their own record rather than a measurement.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, HStack, VStack, Text, Textarea, Button, Spinner, Alert, AlertIcon, Checkbox } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Check } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { listFloor } from '../../lib/oneOnOne'
import { loadFloorWeek } from '../../lib/floorWeek'
import { weeklyPractice } from '../../lib/managerDay'
import { addDays } from '../../lib/localDate'
import {
  RHYTHM, MEASURED, getWeek, saveWeek, mondayOf, weekLabel, isThisWeek,
  weekProgress, toggle, weekHeadline,
} from '../../lib/managerWeek'

export default function ManagerWeek() {
  const { user, profile } = useAuth()
  const dealershipId = profile?.dealership_id || null

  const [week, setWeek] = useState(() => mondayOf())
  const [items, setItems] = useState([])
  const [note, setNote] = useState('')
  const [floor, setFloor] = useState([])
  const [counts, setCounts] = useState({})
  const [state, setState] = useState({ loading: true, saving: false, error: null, saved: false })

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }))
    const [w, f, c] = await Promise.all([
      getWeek(user?.id, week),
      listFloor(dealershipId),
      loadFloorWeek(),
    ])
    setItems(w.data?.items || [])
    setNote(w.data?.note || '')
    setFloor(f.data)
    setCounts(c.counts || {})
    setState({ loading: false, saving: false, error: w.error?.message || f.error?.message || null, saved: false })
  }, [user?.id, dealershipId, week])

  useEffect(() => {
    load()
  }, [load])

  // The role-play count is only honest for the current week: loadFloorWeek
  // reads the last seven days, not an arbitrary one. An older week shows the
  // tick instead of a number rather than a number that is quietly about today.
  const live = isThisWeek(week)
  const drills = useMemo(
    () => weeklyPractice(floor, Object.entries(counts).flatMap(([id, c]) => Array(c.drill || 0).fill({ user_id: id }))),
    [floor, counts]
  )
  const recognised = useMemo(
    () => Object.values(counts).some((c) => Object.values(c || {}).some((n) => n > 0)),
    [counts]
  )

  const roleplayDone = live && floor.length > 0 && drills.done >= floor.length
  const recognitionDone = live && recognised

  const progress = weekProgress({
    items,
    total: RHYTHM.length,
    roleplayDone: live ? roleplayDone : items.includes(MEASURED.roleplay),
    recognitionDone: live ? recognitionDone : items.includes(MEASURED.recognition),
  })

  const save = async () => {
    setState((s) => ({ ...s, saving: true, error: null, saved: false }))
    const { error } = await saveWeek(user?.id, { weekStart: week, items, note })
    setState((s) => ({ ...s, saving: false, error, saved: !error }))
  }

  if (state.loading) return <Spinner size="sm" />

  return (
    <VStack align="stretch" spacing={10} maxW="sheet">
      <Box>
        <Text textStyle="pageEyebrow" mb={1}>
          Every week, as he has it
        </Text>
        <Text textStyle="pageTitle">The coaching rhythm</Text>
        <Text fontSize="lg" color="inkMuted" mt={2} maxW="measure" lineHeight={1.55}>
          {weekHeadline(progress, floor.length)} Two of these the product can see for itself. The
          other four happen on your floor or in your CRM, so they are your own record.
        </Text>
      </Box>

      {state.error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {state.error}
        </Alert>
      )}

      <Box>
        <HStack justify="space-between" align="center" mb={3} flexWrap="wrap" gap={2}>
          <HStack spacing={1}>
            <Button size="xs" variant="ghost" leftIcon={<ChevronLeft size={14} />} onClick={() => setWeek((w) => addDays(w, -7))}>
              Earlier
            </Button>
            <Text fontSize="body" fontWeight={600} minW="14ch" textAlign="center">
              {weekLabel(week)}
            </Text>
            <Button
              size="xs"
              variant="ghost"
              rightIcon={<ChevronRight size={14} />}
              isDisabled={live}
              onClick={() => setWeek((w) => addDays(w, 7))}
            >
              Later
            </Button>
          </HStack>
          <Text fontSize="sm" color="inkDim" fontFamily="mono">
            {state.saving ? 'saving' : state.saved ? 'saved' : `${progress.done} of ${progress.total}`}
          </Text>
        </HStack>

        <VStack align="stretch" spacing={0}>
          {RHYTHM.map((text, i) => {
            const measuredHere = live && (i === MEASURED.roleplay || i === MEASURED.recognition)
            const autoDone = i === MEASURED.roleplay ? roleplayDone : recognitionDone
            return (
              <Box key={text} py={3.5} borderTop="1px solid" borderColor="line">
                <HStack align="flex-start" spacing={3}>
                  {measuredHere ? (
                    <Box pt="2px" color={autoDone ? 'success' : 'inkDim'} flexShrink={0}>
                      <Check size={16} aria-hidden="true" opacity={autoDone ? 1 : 0.35} />
                    </Box>
                  ) : (
                    <Checkbox
                      isChecked={items.includes(i)}
                      onChange={() => {
                        setItems((v) => toggle(v, i))
                        setState((s) => ({ ...s, saved: false }))
                      }}
                      mt="1px"
                    />
                  )}
                  <Box minW={0}>
                    <Text fontSize="body" lineHeight={1.55} maxW="measure">
                      {text}
                    </Text>
                    {measuredHere && (
                      <Text fontSize="sm" color="inkDim" mt={1}>
                        {i === MEASURED.roleplay
                          ? `Counted for you. ${drills.done} of ${drills.of} have practised this week.`
                          : 'Counted for you, on your floor page.'}
                      </Text>
                    )}
                  </Box>
                </HStack>
              </Box>
            )
          })}
        </VStack>

        <Box mt={5}>
          <Text fontSize="sm" color="inkMuted" mb={2}>
            Anything worth remembering about the week.
          </Text>
          <Textarea
            value={note}
            onChange={(e) => {
              setNote(e.target.value)
              setState((s) => ({ ...s, saved: false }))
            }}
            rows={3}
            fontSize="body"
          />
        </Box>

        <HStack mt={4} spacing={3} flexWrap="wrap" rowGap={2}>
          <Button onClick={save} isLoading={state.saving}>
            Save the week
          </Button>
          <Button as={RouterLink} to="/team/" variant="ghost" size="sm">
            See who is putting the work in
          </Button>
        </HStack>
      </Box>
    </VStack>
  )
}
